import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Card, ListItemText, Typography } from '@material-ui/core';
import { NetworkStatus } from '@apollo/client';
import EditIcon from '@material-ui/icons/Edit';

import { ErrorComponent, LoadingSpinner } from '../../../../components';

import { UploadButton } from '../../../../components/UploadButtonComponent';

import { PublishableStatusComponent } from '../../../../components/PublishableStatusComponent';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { Exception } from '@jewelutions/core-common-domain';
import FontUpdateDialog from '../FontUpdateDialog';
import { GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import FontTextSamples from '../../../../components/FontTextSamples/FontTextSamples';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';

interface IProps {
  party: GQLParty;
}

const FontDetails: React.FC<IProps> = ({ party }) => {
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [file, setFile] = useState(null as any);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const fontId = PlatformNavigation.fontDetails.paramsFromPath(useRouteMatch()).fontId;

  const { data, loading, error, networkStatus, refetch } = GQLOperationBuilder.font.getFont({
    fontId: fontId
  });

  const [
    uploadFontMedia,
    { error: errorUploadingFontMedia, loading: uploadingFontMedia }
  ] = GQLOperationBuilder.font.importFontMedia();

  const onUploadError = () => {
    if (!selectedFont) throw new Exception(`Cannot upload as font id is unknown`);
    uploadFontMedia({ variables: { file, fontId: selectedFont.id } });
  };

  if (loading || networkStatus === NetworkStatus.refetch || uploadingFontMedia) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        message={error.message}
        callback={refetch}
      />
    );

  if (errorUploadingFontMedia && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        callback={onUploadError}
        details={errorUploadingFontMedia?.stack}
        message={errorUploadingFontMedia.message}
      />
    );

  const selectedFont = GQLOperationBuilder.font.fontAssetFromResponse(data);
  if (!selectedFont) throw new Exception(`Cannot upload as font id is unknown`);

  return (
    selectedFont && (
      <div>
        <Card style={{ padding: '8px' }}>
          <div style={{ display: 'flex' }}>
            <Typography style={{ marginRight: '2px' }} variant={'h6'}>
              Font Details
            </Typography>
            <EditIcon
              fontSize="small"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowUpdateDialog(true)}
            />
          </div>
          <ListItemText primary="Font name" secondary={selectedFont.font.name} />
          <PublishableStatusComponent text="Font publishable" status={selectedFont.isPublishable} />
          <PublishableStatusComponent text="Font media valid" status={selectedFont.hasMediaValid} />
          <UploadButton
            id="fontDetails1"
            importButtonLabel={'Upload media font'}
            fileTypesAsCsv=".otf"
            onFileSelected={(file: File) => {
              setFile(file);
              uploadFontMedia({ variables: { file, fontId: selectedFont.id } });
            }}
          />
        </Card>
        <FontTextSamples fontAsset={selectedFont} />

        {showUpdateDialog && (
          <FontUpdateDialog
            party={party}
            fontAsset={GQLOperationBuilder.font.fontAssetFromResponse(data)}
            onClose={success => {
              if (success) {
                refetch();
              }
              setShowUpdateDialog(false);
            }}
          ></FontUpdateDialog>
        )}
      </div>
    )
  );
};

export default FontDetails;
